import React, { useEffect, useMemo, useState, useCallback } from "react";
import { AdminSidebar } from "./AdminSidebar";
import { AdministrationOverviewScreen } from "./AdministrationOverviewScreen";
import { AdminPlaceholderScreen } from "./AdminPlaceholderScreen";
import { AdminOrganizationScreen } from "./AdminOrganizationScreen";
import { AdminFeatureFlagsScreen } from "./AdminFeatureFlagsScreen";
import { AdminClassificationsScreen } from "./AdminClassificationsScreen";
import { AdminPayrollTypesScreen } from "./AdminPayrollTypesScreen";
import { AdminInvoicesSettingsScreen } from "./AdminInvoicesSettingsScreen";
import { AdminInvitationsScreen } from "./AdminInvitationsScreen";
import { AdminUsersHubScreen } from "./AdminUsersHubScreen";
import { AdminTradesScreen } from "./AdminTradesScreen";
import { AdminPayrollRatesScreen } from "./AdminPayrollRatesScreen";
import { AdminPayrollSettingsScreen } from "./AdminPayrollSettingsScreen";
import { AdminWorkScheduleScreen } from "./AdminWorkScheduleScreen";
import { AdminWorkTypesScreen } from "./AdminWorkTypesScreen";
import { AdminProductEducationScreen } from "./AdminProductEducationScreen";
import { AdminBrandingScreen } from "./AdminBrandingScreen";
import { AdminSubscriptionScreen } from "./AdminSubscriptionScreen";
import { OrganizationJoinScreen } from "../OrganizationJoinScreen";
import { OrganizationSwitcher } from "../OrganizationSwitcher";
import {  canAccessAdminSection,
  getAdminSection,
  getVisibleAdminSections,
} from "../../helpers/adminSectionAccess";
import { usePermissions } from "../../hooks/usePermissions";
import { useOrganization } from "../../context/OrganizationContext";
import { useHardwareBackHandler } from "../../navigation/HardwareBackContext";
import { DesktopNavProvider } from "../navigation/DesktopNavContext";
import "./AdministrationHub.css";
import "./adminDesktop.css";

export function AdministrationHubScreen({  onBack,
  currentUser,
  initialSection = "overview",
}) {
  const { can } = usePermissions();
  const { getSetting } = useOrganization();
  const [activeSection, setActiveSection] = useState(initialSection);
  const [showJoinOrganization, setShowJoinOrganization] = useState(false);

  const visibleSections = useMemo(
    () => getVisibleAdminSections(can),
    [can],
  );

  const orgName = getSetting("organization.name", "");

  useEffect(() => {
    if (!canAccessAdminSection(can, activeSection)) {
      setActiveSection("overview");
    }
  }, [activeSection, can]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSection]);

  const handleSelectSection = (sectionId) => {
    if (!canAccessAdminSection(can, sectionId)) {
      return;
    }
    setActiveSection(sectionId);
  };

  const handleBackToOverview = () => setActiveSection("overview");

  const handleHardwareBack = useCallback(() => {
    if (showJoinOrganization) {
      setShowJoinOrganization(false);
      return true;
    }

    if (activeSection !== "overview") {
      setActiveSection("overview");
      return true;
    }

    return false;
  }, [activeSection, showJoinOrganization]);

  useHardwareBackHandler(handleHardwareBack);

  const adminNav = useMemo(() => ({ parentLabel: "Administration", activeId: "administration" }), []);
  const sectionMeta = getAdminSection(activeSection);
  const sectionTitle = sectionMeta?.label || "Administration";

  if (showJoinOrganization) {
    return (
      <DesktopNavProvider value={adminNav}>
        <OrganizationJoinScreen
          onBack={() => setShowJoinOrganization(false)}
          onComplete={() => setShowJoinOrganization(false)}
        />
      </DesktopNavProvider>
    );
  }

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <AdministrationOverviewScreen onSelectSection={handleSelectSection} />
        );
      case "organization":
        return <AdminOrganizationScreen onBack={handleBackToOverview} />;
      case "invitations":
        return <AdminInvitationsScreen onBack={handleBackToOverview} />;
      case "users":
        return (
          <AdminUsersHubScreen
            currentUser={currentUser}
            onBack={handleBackToOverview}
          />
        );
      case "trades":
        return <AdminTradesScreen onBack={handleBackToOverview} />;
      case "work_schedule":
        return <AdminWorkScheduleScreen onBack={handleBackToOverview} />;
      case "payroll_settings":
        return (
          <AdminPayrollSettingsScreen onBack={handleBackToOverview} />
        );
      case "payroll":
        return <AdminPayrollRatesScreen onBack={handleBackToOverview} />;
      case "roles":
        return (
          <AdminPlaceholderScreen
            title="Roles & Permissions"
            subtitle="Role matrix and policy controls"
            onBack={handleBackToOverview}
          >
            <p className="admin-placeholder-note">
              The read-only capability matrix and custom role editor are
              planned for a later phase. Role assignment remains in Users →
              User Management.
            </p>
            <p className="admin-placeholder-note" style={{ marginTop: "12px" }}>
              Organization policy toggles are available under Feature Flags.
            </p>
          </AdminPlaceholderScreen>
        );
      case "feature_flags":
        return (
          <AdminFeatureFlagsScreen onBack={handleBackToOverview} />
        );
      case "subscription":
        return <AdminSubscriptionScreen onBack={handleBackToOverview} />;
      case "help_learning":
        return <AdminProductEducationScreen onBack={handleBackToOverview} />;
      case "classifications":
        return (
          <AdminClassificationsScreen onBack={handleBackToOverview} />
        );
      case "payroll_types":
        return (
          <AdminPayrollTypesScreen onBack={handleBackToOverview} />
        );
      case "work_types":
        return <AdminWorkTypesScreen onBack={handleBackToOverview} />;
      case "invoices":
        return (
          <AdminInvoicesSettingsScreen onBack={handleBackToOverview} />
        );
      case "branding":
        return <AdminBrandingScreen onBack={handleBackToOverview} />;
      case "estimates":
      case "projects":
      case "materials":
      case "reports":
      case "notifications":
      case "offline":
      case "integrations":
      case "advanced":
        return (
          <AdminPlaceholderScreen
            title={sectionTitle}
            subtitle="Coming in a future update"
            onBack={handleBackToOverview}
          >
            <p className="admin-placeholder-note">
              {sectionTitle} settings are not yet available in this version of
              CrewPilot.
            </p>
          </AdminPlaceholderScreen>
        );
      default:
        return (
          <AdministrationOverviewScreen onSelectSection={handleSelectSection} />
        );
    }
  };

  return (
    <div className="admin-hub">
      <AdminSidebar
        sections={visibleSections}
        activeSectionId={activeSection}
        onSelectSection={handleSelectSection}
        orgName={orgName}
        onReturn={onBack}
      />

      <div className="admin-hub-main">
        <div className="admin-hub-topbar admin-hub-mobile-back">
          {activeSection !== "overview" ? (
            <button
              type="button"
              className="cockpit-back-btn"
              onClick={handleBackToOverview}
            >
              Back
            </button>
          ) : (
            <button
              type="button"
              className="cockpit-back-btn"
              onClick={onBack}
            >
              Back
            </button>
          )}
        </div>
        <div className="admin-hub-membership-bar" style={{ padding: "0 18px 12px" }}>
          <OrganizationSwitcher />
          <button
            type="button"
            className="kpi-back-btn"
            style={{ marginTop: "8px" }}
            onClick={() => setShowJoinOrganization(true)}
          >
            Join organization
          </button>
        </div>
        <DesktopNavProvider value={adminNav}>{renderSection()}</DesktopNavProvider>
      </div>
    </div>
  );
}
